import { useTranslation } from "react-i18next"
import { Outlet, useRoutes } from "react-router-dom"
import { FC, useEffect, useMemo, useState } from "react"
import axiosInstance from "api/service"
import { useAppDispatch, useAppSelector } from "store/index"
import { useSetLangMutation } from "store/apis/userAPI/index"
import { AppSelectors, setLanguage } from "store/slices/appSlice"
import { useNewsFeed } from "hooks/useNewsFeed"
import DefaultTemplate from "templates/default.tsx"
import { RootTemplate } from "templates/RootTemplate"
import { PageSuspense } from "ui/@page/ui/page-suspense"
import AuthGuard from "@/guards/AuthGuard"
import JailRestrictionGuard from "@/guards/JailRestrictionGuard"
import {
  routerOutsidePathsCommon,
  routerOutsidePathsUnder,
  routerPathsCommon,
  routerPathsUnder
} from "./values"
import {
  AmlPage,
  AppOldVersionPage,
  JailPage,
  LoginPage,
  PageNotFoundPage,
  PrivacyPolicyPage,
  TermsOfUsePage,
  TestDogLottiePage,
  TestLottieApparelsPage,
  UnderMaintenancePage
} from "./pages"

const getRoutes = (isUnder: boolean) => [
  {
    path: "/",
    element: <RootTemplate />,
    children: [
      {
        path: "/",
        element: (
          <AuthGuard>
            <JailRestrictionGuard>
              <DefaultTemplate />
            </JailRestrictionGuard>
          </AuthGuard>
        ),
        children: [
          {
            path: "jail",
            element: <PageSuspense Component={JailPage} />
          },
          ...(isUnder ? routerPathsUnder : routerPathsCommon)
        ]
      },

      {
        element: (
          <AuthGuard>
            <Outlet />
          </AuthGuard>
        ),
        children: [
          ...(isUnder ? routerOutsidePathsUnder : routerOutsidePathsCommon)
        ]
      },

      {
        path: "login",
        element: <PageSuspense Component={LoginPage} />
      },

      {
        path: "terms-of-use",
        element: <PageSuspense style="green" Component={TermsOfUsePage} />
      },
      {
        path: "privacy-policy",
        element: <PageSuspense style="green" Component={PrivacyPolicyPage} />
      },
      {
        path: "aml",
        element: <PageSuspense style="green" Component={AmlPage} />
      },
      {
        path: "test-dog-lottie",
        element: <PageSuspense style="green" Component={TestDogLottiePage} />
      },
      {
        path: "test-lottie-apparels",
        element: (
          <PageSuspense style="green" Component={TestLottieApparelsPage} />
        )
      },
      {
        path: "*",
        element: <PageSuspense Component={PageNotFoundPage} />
      }
    ]
  }
]

const View: FC = () => {
  const dispatch = useAppDispatch()
  const { i18n } = useTranslation()

  const isUnder = useAppSelector(AppSelectors.selectIsUnder())

  const [appVer, setAppVer] = useState<string | null>(null)

  const language = i18n.language

  const [setLang] = useSetLangMutation()

  const routes = useMemo(() => getRoutes(isUnder), [isUnder])
  const routeElement = useRoutes(routes)

  const checkAppVersion = async () => {
    const { data } = await axiosInstance({
      url: "/auth/app-version",
      method: "GET"
    })

    setAppVer(data.version)
  }

  useNewsFeed()

  const shadowSetLang = async (lang: string) => {
    const flag = localStorage.getItem("hasLangUpdated")

    if (flag === "true") return

    localStorage.setItem("hasLangUpdated", "true")

    await setLang(lang)
  }

  useEffect(() => {
    dispatch(setLanguage(language))

    if (language) {
      void shadowSetLang(language)
    }
  }, [language])

  useEffect(() => {
    void checkAppVersion()
  }, [])

  if (import.meta.env.VITE_IS_UNDER_MAINTENANCE === "true") {
    return (
      <PageSuspense
        style="under-maintenance"
        Component={UnderMaintenancePage}
      />
    )
  }

  if (appVer !== null && import.meta.env.VITE_APP_VERSION !== appVer) {
    return (
      <PageSuspense style="under-maintenance">
        <AppOldVersionPage
          currVer={appVer}
          yourVer={import.meta.env.VITE_APP_VERSION}
        />
      </PageSuspense>
    )
  }

  return routeElement
}

export default View
